import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ButtonComp from "../../../../components/Button";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import { getQuotableItems } from "../../../../services/guest";
import { useUserStore } from "../../../../store/useUserStore";
import { useSessionStore } from "../../../../store/useSessionStore";
import { hasIfasCapability } from "../../../../utils/ifasAccess";
import {
  caseActionBarSx,
  caseButtonSx,
  caseFormCardSx,
} from "./caseFormStyles";

type SourceType = "MANUAL" | "POLICY";

interface RequestedServicesFormProps {
  onNext?: () => void;
  onBack?: () => void;
  isEditable?: boolean;
  caseSource?: SourceType;
  benefitSelections?: any[];
  // Saved quotation line items, used on the read-only view side.
  lineItems?: any[];
  claimId?: string;
  claimStatus?: string;
  canEditPackage?: boolean;
  onPackageSelected?: (
    categoryCode: string,
    packageCode: string,
    lineItems: Array<{ quotableItemCode: string; quantity: number; unitPrice: number }>,
  ) => void | Promise<void>;
}

interface RequestedServiceRow {
  id: string;
  // Catalogue code — what the backend resolves the line against. Rows without
  // one cannot be priced or invoiced.
  code?: string;
  serviceName: string;
  amount: string;
  quantity?: number;
  unitPrice?: number;
  taxCode?: string;
  locked?: boolean;
}

// Quotable items come from the catalogue (Catalogue & Pricing), never from a
// hardcoded list: the price a client is quoted has to be the price finance
// approved, and a consultant must not be able to invent one at the counter.

// Input field ka style — image ke "Document Type" dropdown jaisa (orange border, rounded)
const selectFieldSx = {
  borderRadius: 2,
  backgroundColor: "#FFFFFF",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#F0A868",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C85A00",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C85A00",
  },
};

const formatAmount = (value: any) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return text.toUpperCase().startsWith("R") ? text : `R${text}`;
};

// Normalizes a QuoteLineItem (creation store, camelCase) or a saved
// quotation line item from the backend (snake_case) into a display row.
const lineItemToRow = (li: any, index: number): RequestedServiceRow => {
  const name =
    li?.name ??
    li?.description ??
    li?.quotableItemName ??
    li?.quotable_item_name ??
    li?.serviceName ??
    "Service";
  const quantity = Number(li?.quantity ?? li?.qty ?? 1) || 1;
  const unitPrice = Number(li?.unitPrice ?? li?.unit_price ?? 0) || 0;
  const code =
    li?.code ??
    li?.quotableItemCode ??
    li?.quotable_item_code ??
    `${name}-${index}`;
  const lineTotal = unitPrice * quantity;

  return {
    id: `${code}-${index}`,
    code,
    serviceName: quantity > 1 ? `${name} x${quantity}` : name,
    amount: formatAmount(lineTotal),
    quantity,
    unitPrice,
    taxCode: li?.taxCode ?? li?.tax_code,
  };
};

export default function RequestedServicesForm({
  onNext,
  onBack,
  isEditable: _isEditable = true,
  caseSource: _caseSource = "MANUAL",
  benefitSelections = [],
  lineItems = [],
  claimId,
  claimStatus,
  canEditPackage = false,
  onPackageSelected,
}: RequestedServicesFormProps) {
  const { packageSelection, setPackageSelection } = useCaseCreationStore();

  // Only a role that may maintain the catalogue can depart from the approved
  // price. Everyone else quotes what finance published — the consultant at the
  // counter must not be able to set their own figure (SOP 12.1 separation of duty).
  const storeRole = useUserStore((s: any) => s.role);
  const { userRole } = useSessionStore();
  const canOverridePrice = hasIfasCapability(userRole || storeRole, "catalogueManage");
  const sourceType: SourceType =
    !_isEditable || benefitSelections.length
      ? _caseSource
      : packageSelection.sourceType || _caseSource || "MANUAL";

  // Dropdown + amount input ki current selection (Add hone se pehle)
  const [selectedService, setSelectedService] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("");

  // Catalogue-driven options. Loaded once; the price shown is the approved
  // base price, so the consultant picks an item rather than typing a figure.
  const [catalogueItems, setCatalogueItems] = useState<
    { code: string; name: string; basePrice: number; taxCode?: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    getQuotableItems().then((response: any) => {
      if (cancelled) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      setCatalogueItems(
        rows.map((r: any) => ({
          code: r.code,
          name: r.name,
          basePrice: Number(r.basePrice ?? 0),
          // Carried through to the line item: Package/Pricing derives VAT from
          // this, so dropping it silently zero-rates the whole quotation.
          taxCode: r.taxCode,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const packageFromCatalogueItem = (item?: { code: string; name: string }) => {
    const code = String(item?.code || "").trim().toUpperCase();
    const packageCodeByCatalogueCode: Record<string, { categoryCode: string; packageCode: string }> = {
      "CAT-A": { categoryCode: "CATEGORY_A", packageCode: "CATEGORY_A_SET" },
      "CAT-B": { categoryCode: "CATEGORY_B", packageCode: "CATEGORY_B_SET" },
      "CAT-C": { categoryCode: "CATEGORY_C", packageCode: "CATEGORY_C_SET" },
      "CAT-D": { categoryCode: "CATEGORY_D", packageCode: "CATEGORY_D_SET" },
    };
    return packageCodeByCatalogueCode[code] || null;
  };

  // Seeded from the store, not from an empty array: the rows have to survive
  // stepping forward to Package/Pricing and back again.
  const [requestedServices, setRequestedServices] = useState<
    RequestedServiceRow[]
  >(() => {
    if (packageSelection.lineItems?.length) {
      return packageSelection.lineItems.map((li: any) => ({
      id: `${li.code}-${li.quantity}`,
      code: li.code,
      serviceName: li.name ?? li.code,
      amount: formatAmount(li.unitPrice),
      quantity: li.quantity ?? 1,
      unitPrice: Number(li.unitPrice ?? 0),
      taxCode: li.taxCode,
      }));
    }
    return (lineItems ?? []).map(lineItemToRow);
  });

  // Single place that writes the table into the store, so Package/Pricing and
  // the final submit read exactly what is on screen.
  const persistRows = (rows: RequestedServiceRow[]) => {
    setRequestedServices(rows);
    setPackageSelection({
      // Only catalogue-backed rows become line items. A row without a code
      // cannot be resolved or invoiced, so it must never reach the payload.
      lineItems: rows
        .filter((row): row is RequestedServiceRow & { code: string } =>
          Boolean(row.code)
        )
        .map((row) => ({
          code: row.code,
          name: row.serviceName,
          quantity: row.quantity ?? 1,
          unitPrice: row.unitPrice ?? 0,
          taxCode: row.taxCode,
        })),
    });
  };

  const claimScheduleRows = useMemo(
    () =>
      benefitSelections.map((benefit, index) => {
        const isProduct = Boolean(
          benefit?.productId ||
            benefit?.product_id ||
            benefit?.productName ||
            benefit?.product_name,
        );
        const name =
          benefit?.productName ||
          benefit?.product_name ||
          benefit?.supplementaryName ||
          benefit?.supplementary_name ||
          "Selected benefit";
        const cashAmount = benefit?.cash ?? benefit?.cash_amount ?? "";
        const serviceAmount = benefit?.service ?? benefit?.service_amount ?? "";
        const amountParts = [
          cashAmount ? `Cash: ${formatAmount(cashAmount)}` : "",
          serviceAmount ? `Service: ${formatAmount(serviceAmount)}` : "",
        ].filter(Boolean);

        return {
          id:
            benefit?.scheduleBenefitId ||
            benefit?.schedule_benefit_id ||
            `${name}-${index}`,
          serviceName: `${isProduct ? "Main Plan" : "Top Up"} - ${name}`,
          amount: amountParts.length ? amountParts.join(" | ") : "R0",
          locked: true,
        };
      }),
    [benefitSelections],
  );

  // Editable creation reads the catalogue-driven rows (store-backed, add/remove).
  // Read-only view shows the line items saved on the fetched quotation.
  const displayServices = claimScheduleRows.length
    ? claimScheduleRows
    : (_isEditable || (canEditPackage && requestedServices.length > 0))
    ? requestedServices
    : (lineItems ?? []).map(lineItemToRow);

  const normalizedClaimStatus = String(claimStatus || "").toUpperCase();
  const isReviewedOrLater = [
    "REVIEWED",
    "APPROVED",
    "CASH_PAID",
    "SERVICE_PAID",
    "PAID",
    "CLOSED",
  ].includes(normalizedClaimStatus);

  const emptyServicesMessage = (() => {
    if (sourceType !== "POLICY") {
      return "No services added yet";
    }

    if (!claimId) {
      return "No linked claim is available for this case yet.";
    }

    if (!isReviewedOrLater) {
      return "Claim schedule benefits will show after the claim is reviewed.";
    }

    return "No claim schedule benefits found.";
  })();

  const handleSourceTypeChange = (nextSourceType: SourceType) => {
    setPackageSelection({ sourceType: nextSourceType });
  };

  const handleAddService = async () => {
    if (!selectedService) return;
    const item = catalogueItems.find((c) => c.code === selectedService);
    if (!item) return;

    // A typed amount is honoured only for a role that may maintain the catalogue.
    // For everyone else the approved price is used regardless of what is in the
    // box, so the figure on the quotation is always the one finance published.
    const typed = Number(String(selectedAmount).replace(/[^0-9.]/g, ""));
    const unitPrice =
      canOverridePrice && Number.isFinite(typed) && typed > 0
        ? typed
        : item.basePrice;

    const newRow: RequestedServiceRow = {
      id: `${item.code}-${Date.now()}`,
      code: item.code,
      serviceName: item.name,
      amount: formatAmount(unitPrice),
      quantity: 1,
      unitPrice,
      taxCode: item.taxCode,
    };

    const nextRows = [...requestedServices, newRow];
    persistRows(nextRows);
    const selectedPackage = packageFromCatalogueItem(item);
    const existingPackage = requestedServices
      .map((row) =>
        packageFromCatalogueItem({ code: row.code || "", name: row.serviceName }),
      )
      .find(Boolean);
    const categoryCode =
      selectedPackage?.categoryCode ||
      packageSelection.funeralCategoryCode ||
      existingPackage?.categoryCode;
    const packageCode =
      selectedPackage?.packageCode ||
      packageSelection.funeralPackageCode ||
      existingPackage?.packageCode;
    if (categoryCode && packageCode) {
      await onPackageSelected?.(
        categoryCode,
        packageCode,
        nextRows
          .filter((row) => Boolean(row.code))
          .map((row) => ({
            quotableItemCode: row.code as string,
            quantity: row.quantity ?? 1,
            unitPrice: row.unitPrice ?? 0,
          })),
      );
    }
    setSelectedService("");
    setSelectedAmount("");
  };

  const handleRemoveService = (id: string) => {
    persistRows(requestedServices.filter((row) => row.id !== id));
  };

  const canMaintainRequestedServices = _isEditable || canEditPackage;
  const isFormLocked = !canMaintainRequestedServices || claimScheduleRows.length > 0;
  const hasFuneralPackage = Boolean(
    packageSelection.funeralCategoryCode && packageSelection.funeralPackageCode,
  ) || requestedServices.some((row) =>
    Boolean(packageFromCatalogueItem({ code: row.code || "", name: row.serviceName })),
  );
  const isAddDisabled = !selectedService || isFormLocked;

  return (
    <Paper elevation={0} sx={caseFormCardSx}>
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: 3,
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 2,
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: "#FCFCFD",
          }}
        >
          <Typography
            fontSize={18}
            fontWeight={800}
            color="#111827"
            textAlign="left"
          >
            Requested Services
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} mt={2}>
            <Chip
              clickable
              label="Cash Client"
              onClick={() => {
                if (!isFormLocked) handleSourceTypeChange("MANUAL");
              }}
              sx={{
                bgcolor: sourceType === "MANUAL" ? "#FFF4EB" : "#F3F4F6",
                color: sourceType === "MANUAL" ? "#C85A00" : "#4B5563",
                fontWeight: 800,
                borderRadius: 999,
                px: 1,
              }}
            />
            <Chip
              clickable
              label="Policy Holder"
              onClick={() => {
                if (!isFormLocked) handleSourceTypeChange("POLICY");
              }}
              sx={{
                bgcolor: sourceType === "POLICY" ? "#EEF2FF" : "#F3F4F6",
                color: sourceType === "POLICY" ? "#3730A3" : "#4B5563",
                fontWeight: 800,
                borderRadius: 999,
                px: 1,
              }}
            />
          </Stack>

        </Box>

        {/* Select + Amount + Add row — image ke "Document Type + Document + Add" jaisa */}
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 2.5,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-end" }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                fontSize={13}
                fontWeight={700}
                color="#111827"
                mb={0.75}
              >
                Service Type
              </Typography>
              <Select
                fullWidth
                displayEmpty
                size="small"
                value={selectedService}
                onChange={(e) => setSelectedService(String(e.target.value))}
                disabled={isFormLocked}
                sx={selectFieldSx}
              >
                <MenuItem value="" disabled>
                  Select service
                </MenuItem>
                {catalogueItems.map((item) => (
                  <MenuItem
                    key={item.code}
                    value={item.code}
                    disabled={
                      !canMaintainRequestedServices ||
                      (canEditPackage && !_isEditable && !hasFuneralPackage && !packageFromCatalogueItem(item))
                    }
                  >
                    {item.name}
                    {item.basePrice > 0 ? ` — ${formatAmount(item.basePrice)}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                fontSize={13}
                fontWeight={700}
                color="#111827"
                mb={0.75}
              >
                Amount
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  canOverridePrice
                    ? "Catalogue price (override)"
                    : "Set from catalogue"
                }
                value={
                  canOverridePrice
                    ? selectedAmount
                    : selectedService
                    ? formatAmount(
                        catalogueItems.find((c) => c.code === selectedService)
                          ?.basePrice ?? ""
                      )
                    : ""
                }
                onChange={(e) => setSelectedAmount(e.target.value)}
                disabled={isFormLocked || !canOverridePrice}
                sx={{
                  "& .MuiOutlinedInput-root": selectFieldSx,
                }}
              />
            </Box>

            <ButtonComp
              variant="contained"
              onClick={handleAddService}
              disabled={isAddDisabled}
              sx={{ ...caseButtonSx, height: 40, whiteSpace: "nowrap" }}
            >
              Add
            </ButtonComp>
          </Stack>
        </Box>

        {/* Table — Add hote hi yahan row dikhegi, image ke document table jaisa */}
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: "#374151" }}>
                  Service Name
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 160 }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 140 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody sx={{ backgroundColor: "#FFFFFF" }}>
              {displayServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography fontSize={14} color="#6B7280" fontWeight={600}>
                      {emptyServicesMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayServices.map((service) => (
                  <TableRow
                    key={service.id}
                    sx={{
                      "& td": {
                        py: 2.25,
                        borderBottom: "1px solid #E5E7EB",
                      },
                    }}
                  >
                    <TableCell sx={{ color: "#111827", fontWeight: 700 }}>
                      {service.serviceName}
                    </TableCell>
                    <TableCell sx={{ color: "#4B5563", fontWeight: 700 }}>
                      {service.amount}
                    </TableCell>
                    <TableCell>
                      <Chip
                        clickable
                        size="small"
                        label={service.locked ? "Claim Schedule" : "Remove"}
                        onClick={() => handleRemoveService(service.id)}
                        disabled={Boolean(service.locked)}
                        sx={{
                          bgcolor: service.locked ? "#EEF2FF" : "#FEE2E2",
                          color: service.locked ? "#3730A3" : "#B91C1C",
                          fontWeight: 800,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={caseActionBarSx}
      >
        {onBack ? (
          <ButtonComp variant="outlined" onClick={onBack} sx={caseButtonSx}>
            Back
          </ButtonComp>
        ) : (
          <Box />
        )}

        {onNext ? (
          <ButtonComp variant="contained" onClick={onNext} sx={caseButtonSx}>
            Next
          </ButtonComp>
        ) : null}
      </Stack>
    </Paper>
  );
}
